import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {MultiSelect} from 'primereact/multiselect'
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {useAuth} from '../authentication/AuthProvider'
import DialogWrapper from '../new-table/DialogWrapper'
import IconWrapper from '../new-table/IconWrapper'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {useListPathways} from '../pathway/query-keys/PathwayKeysManager'
import {
    filterEventTypes,
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterChangeListener,
    onFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {useDatatableContext} from './DataTableContext'

const StyledFilterByPathway = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateCommons, translateOptions} =
        TranslateUtils()

    const {user} = useAuth()
    const {tableType} = useDatatableContext()

    //#region URL Query Params
    const query = useQueryParams()
    const queryParamsAdvancedFilter = useMemo(() => {
        const selectedPathwayIds = query?.get('pathwayId')
            ? [parseInt(query?.get('pathwayId'))]
            : []

        return selectedPathwayIds
    }, [query])
    //#endregion

    const panelVisible = useSignal(false)
    const selectedItems = useSignal(
        queryParamsAdvancedFilter ? queryParamsAdvancedFilter : [],
    )
    const filterByPathwayRef = useRef(null)

    const {data: pathwayOptions} = useListPathways(
        user?.id,
        true,
        {},
        !!user?.id,
    )

    useEffect(() => {
        if (
            isArrayAndNotEmpty(pathwayOptions) &&
            isArrayAndNotEmpty(queryParamsAdvancedFilter)
        ) {
            onFilterChangeListener(tableType, filterEventTypes.pathway, {
                pathwayIds: queryParamsAdvancedFilter,
            })

            let selectedPathways = []

            selectedPathways = pathwayOptions?.filter(item =>
                queryParamsAdvancedFilter.includes(item.value),
            )

            onFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.PATHWAY_FILTER_EXPLAIN_CHANGE,
                selectedPathways,
            )
        }
    }, [pathwayOptions, queryParamsAdvancedFilter])

    const handleShowForm = value => {
        blockOverflownScroll()

        filterByPathwayRef.current.onShow()
    }

    const handleHideForm = () => {
        unblockOverflownScroll()

        filterByPathwayRef.current.onHide()
    }

    const handleSubmit = () => {
        onFilterChangeListener(tableType, filterEventTypes.pathway, {
            pathwayIds: selectedItems.value,
        })

        let filteredSelectedItems = []

        if (isArrayAndNotEmpty(selectedItems.value)) {
            filteredSelectedItems = pathwayOptions?.filter(item =>
                selectedItems.value.includes(item.value),
            )
        }

        onFilterExplainChangeListener(
            tableType,
            filterExplainEventTypes.PATHWAY_FILTER_EXPLAIN_CHANGE,
            filteredSelectedItems,
        )

        handleHideForm()
    }

    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value === null ? [] : e.value
    }

    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }

    useEffect(() => {
        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_PATHWAY_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )

            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_PATHWAY_FILTER,
            )
        }
    }, [])

    return (
        <DialogWrapper
            header={translateDatatable('Filter by pathway')}
            ref={filterByPathwayRef}
            onSubmit={handleSubmit}>
            <div className="flex flex-column gap-2">
                <p className="m-0 p-0 mb-3">
                    {translateDatatable(
                        'Selecting multiple pathways will display the patient list according to the chosen pathways',
                    )}
                </p>
                <MultiSelect
                    className="w-full h-3rem p-inputtext p-0 font-bold"
                    options={
                        isArrayAndNotEmpty(pathwayOptions)
                            ? translateOptions(pathwayOptions)
                            : []
                    }
                    placeholder={translateCommons('Select an option')}
                    defaultValue={[]}
                    maxSelectedLabels={3}
                    display={selectedItems.value?.length < 4 ? 'chip' : 'comma'}
                    value={selectedItems.value || []}
                    onChange={handleChangeSelectedItems}
                    itemTemplate={item => {
                        return (
                            <div className="flex align-items-center">
                                <div className="mx-2">
                                    <img
                                        src={item.data}
                                        style={{
                                            height: '25px',
                                            width: '25px',
                                        }}
                                        alt={`${item.label}`}
                                    />
                                </div>
                                <div>{item.label}</div>
                            </div>
                        )
                    }}
                    panelFooterTemplate={() =>
                        renderPanelFooterTemplate(selectedItems.value)
                    }
                    {...panelVisiblilityProps}
                />

                <p className="font-bold">
                    You have selected{' '}
                    <span className="text-darkcyan">
                        {selectedItems.value?.length < 1
                            ? 'no'
                            : selectedItems.value?.length}{' '}
                        pathway(s)
                    </span>{' '}
                    out of{' '}
                    <span className="text-darkcyan">
                        {pathwayOptions?.length} available pathway(s)
                    </span>
                </p>

                {selectedItems.value?.length > 0 && (
                    <div>
                        <div className="flex flex-wrap align-items-center gap-1">
                            {selectedItems.value?.map(selectedItemId => {
                                const pathwayItem = pathwayOptions?.find(
                                    item => item.value === selectedItemId,
                                )

                                if (!pathwayItem) {
                                    return null
                                }

                                return (
                                    <div
                                        key={selectedItemId}
                                        className=" m-0 p-0 bg-white p-2 border-round-lg border-lightgreen mr-2">
                                        <IconWrapper
                                            id={pathwayItem.value + 'filterBy'}
                                            content={pathwayItem.label}
                                            icon={
                                                <img
                                                    src={pathwayItem.data}
                                                    style={{
                                                        height: '25px',
                                                        width: '25px',
                                                    }}
                                                    alt={`${pathwayItem.label}`}
                                                />
                                            }
                                            tooltip={false}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </DialogWrapper>
    )
})

export default StyledFilterByPathway
